import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "./meta.mjs";
import { ExperienceFlowEnum, PaymentMethodEnum } from "./types.mjs";
import { z as z$1 } from "zod";

// src/models/referral.ts
const SPONSORED_EXPIRATION_BANNER_TITLE_DEFAULT = "Your sponsored access ends on";
const SPONSORED_EXPIRATION_BANNER_SUBTITLE_DEFAULT = "Pick your subscription plan now. Billing starts when sponsorship ends.";
const paymentMethodConfigIndividualSchema = z$1.object({
	stripeCouponId: zodFlavorId().nullable().default(null),
	trialDays: z$1.number().int().min(0).max(30).default(0)
});
const paymentMethodConfigSponsoredSchema = z$1.object({
	consultationIncluded: z$1.boolean().default(false),
	expirationBannerTitle: z$1.string().trim().nullable().optional(),
	expirationBannerSubtitle: z$1.string().trim().nullable().optional(),
	endDate: z$1.any().transform(zodTransformFirestoreTimestampToDate).nullable(),
	durationMonths: z$1.number().nullable()
}).refine((data) => data.endDate || data.durationMonths, {
	message: "Either end date or duration months must be provided",
	path: ["endDate", "durationMonths"]
});
const paymentMethodConfigSchema = z$1.object({
	["default"]: paymentMethodConfigIndividualSchema.nullable().default(null),
	["sponsored"]: paymentMethodConfigSponsoredSchema.nullable().default(null)
});
const experienceFlowConfigDefaultSchema = z$1.strictObject({});
const experienceFlowConfigSponsoredSchema = z$1.strictObject({
	sponsorScreenTitle: z$1.string(),
	sponsorScreenSubtitle: z$1.string(),
	sponsorScreenFeatures: z$1.array(z$1.string())
});
const experienceFlowConfigSchema = z$1.strictObject({
	["default"]: meta(experienceFlowConfigDefaultSchema.nullable().default(null), { bqType: "stringified_map" }),
	["astrazeneca"]: meta(experienceFlowConfigSponsoredSchema.nullable().default(null), { bqType: "stringified_map" })
});
const referralProgramSchema = z$1.strictObject({
	id: zodFlavorId(),
	referralCode: z$1.string(),
	name: z$1.string().nullable(),
	organizationInfo: z$1.object({ name: z$1.string() }).nullable(),
	individualInfo: z$1.object({
		firstName: meta(z$1.string(), { pii: { category: "name" } }),
		lastName: meta(z$1.string(), { pii: { category: "name" } }),
		title: z$1.string()
	}).nullable(),
	activeTimeFrame: z$1.object({
		startDate: z$1.any().transform(zodTransformFirestoreTimestampToDate),
		endDate: z$1.any().transform(zodTransformFirestoreTimestampToDate).nullable()
	}),
	userCount: z$1.number(),
	experienceFlow: z$1.enum(Object.values(ExperienceFlowEnum)),
	paymentMethod: z$1.enum(Object.values(PaymentMethodEnum)),
	paymentMethodConfigs: paymentMethodConfigSchema,
	experienceFlowConfigs: experienceFlowConfigSchema,
	createdAt: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	updatedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate)
}).refine((data) => data.paymentMethodConfigs[data.paymentMethod], {
	message: "Payment method config is required",
	path: ["paymentMethodConfigs"]
}).refine((data) => data.experienceFlowConfigs[data.experienceFlow], {
	message: "Experience flow config is required",
	path: ["experienceFlowConfigs"]
});
/** Display label for admin UI and denormalized list shards. */
function referralProgramTypeFromInfo(program) {
	if (program.individualInfo) return "individual";
	if (program.organizationInfo) return "organization";
	return null;
}
function getReferralProgramDisplayName(program) {
	if (program.organizationInfo) return program.organizationInfo.name;
	if (program.individualInfo) return `${program.individualInfo.firstName} ${program.individualInfo.lastName}`;
	return program.name || program.id;
}
/**
* Referral programs collection definition
* Path: referral_programs/{referralProgramId}
*/
const ReferralPrograms = defineFirestoreCollection("referral_programs", referralProgramSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "referral_programs"
});

export { ReferralPrograms, SPONSORED_EXPIRATION_BANNER_SUBTITLE_DEFAULT, SPONSORED_EXPIRATION_BANNER_TITLE_DEFAULT, experienceFlowConfigSchema, getReferralProgramDisplayName, paymentMethodConfigSchema, referralProgramSchema, referralProgramTypeFromInfo };
//# sourceMappingURL=referral.mjs.map